import { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsKonamiUnlocked, setUnlocked, resetKonami } from '@store/features/konamiSlice';

// Der klassische Konami-Code
const KONAMI_CODE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'b',
  'a'
];

/**
 * Hook zur Erkennung des Konami-Codes - mit Redux und RTK persist
 * @param onUnlock Funktion, die ausgeführt wird, wenn der Konami-Code eingegeben wurde
 * @returns Objekt mit dem aktuellen Status (isUnlocked)
 */
export const useKonamiCode = (onUnlock?: () => void) => {
  // Redux-Status und Dispatch
  const isUnlocked = useSelector(selectIsKonamiUnlocked);
  const dispatch = useDispatch();
  
  // Refs für die Eingabesequenz und Timeout-ID
  const sequenceRef = useRef<string[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Funktion zum Zurücksetzen der Eingabesequenz nach Timeout
  const resetSequence = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      sequenceRef.current = [];
    }, 5000);
  }, []);

  // Keydown-Event-Handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      
      // Aktuelle Sequenz aktualisieren
      const newSequence = [...sequenceRef.current, key];
      
      // Prüfen auf teilweise Übereinstimmung mit dem Konami-Code
      const partialMatch = KONAMI_CODE.slice(0, newSequence.length).every(
        (code, i) => code.toLowerCase() === newSequence[i].toLowerCase()
      );
      
      if (!partialMatch) {
        // Keine Übereinstimmung: zurücksetzen
        sequenceRef.current = [];
        return;
      }
      
      // Sequenz aktualisieren
      sequenceRef.current = newSequence;
      resetSequence(); // Timeout für Reset starten
      
      // Bei vollständiger Übereinstimmung: Status umschalten (toggle) und Callback ausführen
      if (newSequence.length === KONAMI_CODE.length) {
        // Redux-Zustand umschalten (toggle) - aktuellen Wert invertieren
        dispatch(setUnlocked(!isUnlocked));
        
        // Sequenz zurücksetzen
        sequenceRef.current = [];
        
        // Callback ausführen, wenn vorhanden
        if (onUnlock) onUnlock();
      }
    };
    
    // Event-Listener hinzufügen
    window.addEventListener('keydown', handleKeyDown);
    
    // Cleanup beim Unmounten
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [dispatch, resetSequence, onUnlock, isUnlocked]);
  
  // Funktion zum manuellen Zurücksetzen des Zustands
  const reset = useCallback(() => {
    dispatch(resetKonami());
  }, [dispatch]);
  
  return { isUnlocked, reset };
};
